/**
 * The target of a tap-to-route walk (integration plan slice 4): a ring on the
 * tapped tile while the route runs. Its screen position is read every
 * animation frame through `locate` (the renderer's `projectTile`, through the
 * shell), as the status badges read theirs, so it stays on its tile while the
 * camera follows the leader. No text; hidden with no target.
 */
import { useEffect, useRef } from 'react';
import type { Pos } from '@ab/game-core';
import type { Strings } from '@ab/session';
import { text } from './text.ts';
import './overlays.css';

export type RouteMarkerProps = {
  readonly target: Pos | null;
  readonly strings: Strings;
  /** Where a tile's centre on the ground is on screen now, CSS pixels. */
  readonly locate: (pos: Pos) => { readonly x: number; readonly y: number; readonly visible: boolean };
};

export const RouteMarker = ({ target, strings, locate }: RouteMarkerProps) => {
  const el = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (target === null) return;
    let frame = 0;
    const place = (): void => {
      const at = locate(target);
      const node = el.current;
      if (node !== null) {
        node.hidden = !at.visible;
        node.style.transform = `translate(${at.x.toFixed(1)}px, ${at.y.toFixed(1)}px) translate(-50%, -50%)`;
      }
      frame = requestAnimationFrame(place);
    };
    place();
    return () => {
      cancelAnimationFrame(frame);
    };
  }, [target, locate]);
  return target === null ? null : <div ref={el} className="ab-route-marker" data-testid="route-marker" role="img" aria-label={text(strings, 'ui.route.target')} hidden />;
};
